import {
  GROUP_CHATS_FETCH_REQUEST,
  GROUP_CHATS_FETCH_SUCCESS,
  GROUP_CHATS_FETCH_FAIL,
  GROUP_CHAT_MESSAGES_FETCH_REQUEST,
  GROUP_CHAT_MESSAGES_FETCH_SUCCESS,
  GROUP_CHAT_MESSAGES_FETCH_FAIL,
  GROUP_CHAT_SEND_SUCCESS,
  GROUP_CHAT_MESSAGES_NEW,
  GROUP_CHAT_CREATE_SUCCESS,
  GROUP_CHAT_MESSAGES_CLEAR,
  GROUP_CHATS_UNREAD_COUNT_SUCCESS,
  GROUP_CHAT_ADD_MEMBERS_SUCCESS,
  GROUP_CHAT_UPDATE_SUCCESS,
  GROUP_CHAT_LEAVE_SUCCESS,
  GROUP_CHAT_DELETE_SUCCESS,
  GROUP_CHAT_MESSAGE_DELETE_SUCCESS,
  GROUP_READ_RECEIPT,
  GROUP_CHAT_MESSAGE_REACTION_SUCCESS,
  GROUP_CHAT_MESSAGE_REACTION_UPDATE,
  GROUP_CHAT_MESSAGE_EDIT_SUCCESS,
  GROUP_CHAT_MESSAGE_EDITED_REMOTE,
  GROUP_CHAT_MEMBERS_ADDED,
  GC_TYPING,
  GC_TYPING_CLEAR,
} from 'mastodon/actions/group_chats';
import type {
  ApiGroupChatJSON,
  ApiGroupChatMessageJSON,
} from 'mastodon/api_types/group_chats';
import { compareId } from 'mastodon/compare_id';

interface GroupChatState {
  list: ApiGroupChatJSON[];
  listLoading: boolean;
  listError: string | null;

  activeGroupChatId: string | null;
  messages: ApiGroupChatMessageJSON[];
  messagesLoading: boolean;
  messagesError: string | null;
  messagesHasMore: boolean;

  unreadCount: number;

  // groupChatId -> { accountId -> lastReadMessageId }
  memberReadPositions: Record<string, Record<string, string>>;

  // groupChatId -> accountIds currently typing
  typingAccounts: Record<string, string[]>;

  // Single messages locally deleted (epoch ms). The gap-safe poll cursor
  // (MED-17) re-fetches a recent overlap window, so a stale in-flight poll must
  // not resurrect a just-deleted message. Short-TTL tombstone (mirrors the DM
  // reducer).
  deletedMessages: Record<string, number>;

  // M-9: in-thread system notices for members_added streaming events.
  // groupChatId -> bounded list (newest last).
  memberNotices: Record<
    string,
    Array<{
      id: string;
      addedByAccountId: string;
      addedCount: number;
      at: number;
    }>
  >;
}

const initialState: GroupChatState = {
  list: [],
  listLoading: false,
  listError: null,

  activeGroupChatId: null,
  messages: [],
  messagesLoading: false,
  messagesError: null,
  messagesHasMore: true,

  unreadCount: 0,

  memberReadPositions: {},
  typingAccounts: {},
  deletedMessages: {},
  memberNotices: {},
};

type Action = { type: string; [key: string]: unknown };

// Single-message deletion tombstone (mirrors the DM reducer, MED-17 F2).
const DELETED_MESSAGE_TTL_MS = 60 * 1000;
const isTombstoned = (
  deleted: Record<string, number>,
  id: string,
  now: number,
) => deleted[id] !== undefined && now - deleted[id] < DELETED_MESSAGE_TTL_MS;
const pruneDeletedMessages = (deleted: Record<string, number>, now: number) => {
  const next: Record<string, number> = {};
  let changed = false;
  for (const [id, ts] of Object.entries(deleted)) {
    if (now - ts < DELETED_MESSAGE_TTL_MS) next[id] = ts;
    else changed = true;
  }
  return changed ? next : deleted;
};

export const groupChatsReducer = (
  state: GroupChatState = initialState,
  action: Action,
): GroupChatState => {
  switch (action.type) {
    case GROUP_CHATS_FETCH_REQUEST:
      return { ...state, listLoading: true, listError: null };

    case GROUP_CHATS_FETCH_SUCCESS: {
      const groupChats = action.groupChats as ApiGroupChatJSON[];
      // Cold-load hydration of "seen by" receipts: the serialized per-member
      // read_states seed memberReadPositions, which was previously populated
      // ONLY by live streaming receipts — so after a reload no "seen by"
      // avatars appeared until someone read something new. Never regress a
      // fresher streaming position with a stale list fetch (snowflake ids
      // compare numerically; fall back to the incoming value on parse
      // failure — an unparseable position is better than none).
      const memberReadPositions = { ...state.memberReadPositions };
      for (const gc of groupChats) {
        for (const rs of gc.read_states ?? []) {
          const existing = memberReadPositions[gc.id]?.[rs.account_id];
          if (existing !== undefined) {
            const [a, b] = [BigInt(existing), BigInt(rs.last_read_message_id)];
            if (a >= b) continue;
          }
          memberReadPositions[gc.id] = {
            ...memberReadPositions[gc.id],
            [rs.account_id]: rs.last_read_message_id,
          };
        }
      }
      return {
        ...state,
        listLoading: false,
        list: groupChats,
        memberReadPositions,
      };
    }

    case GROUP_CHATS_FETCH_FAIL:
      return { ...state, listLoading: false, listError: String(action.error) };

    case GROUP_CHAT_MESSAGES_FETCH_REQUEST:
      return {
        ...state,
        messagesLoading: true,
        messagesError: null,
        activeGroupChatId: action.groupChatId as string,
        messages: action.maxId ? state.messages : [],
        messagesHasMore: true,
      };

    case GROUP_CHAT_MESSAGES_FETCH_SUCCESS: {
      const incoming = action.messages as ApiGroupChatMessageJSON[];
      let merged: ApiGroupChatMessageJSON[];
      if (action.maxId) {
        // Older-page prepend (scroll-up history): dedupe by id — a boundary
        // row can be re-served when a send lands mid-fetch (DM parity).
        const existingIds = new Set(state.messages.map((m) => m.id));
        const fresh = incoming.filter((m) => !existingIds.has(m.id));
        merged = [...fresh, ...state.messages];
      } else {
        merged = incoming;
      }
      return {
        ...state,
        messagesLoading: false,
        messages: merged,
        messagesHasMore: incoming.length >= 30,
      };
    }

    case GROUP_CHAT_MESSAGES_FETCH_FAIL:
      return {
        ...state,
        messagesLoading: false,
        messagesError: String(action.error),
      };

    case GROUP_CHAT_SEND_SUCCESS: {
      const msg = action.message as ApiGroupChatMessageJSON;
      const inChat = state.activeGroupChatId === (action.groupChatId as string);
      // The 3s poll / streaming push can deliver our OWN message before this
      // dispatch (the one-round-trip send made that race real, 2026-08-08:
      // own messages rendered twice until refresh). If the id is already
      // present, REPLACE it — the send's copy carries the authoritative
      // plaintext (we cannot decrypt our own sender-key ciphertext).
      const alreadyPresent =
        inChat && state.messages.some((m) => m.id === msg.id);
      const nextMessages = !inChat
        ? state.messages
        : alreadyPresent
          ? state.messages.map((m) => (m.id === msg.id ? msg : m))
          : [...state.messages, msg];
      return {
        ...state,
        messages: nextMessages,
        // Update last_message in list
        list: state.list.map((gc) =>
          gc.id === (action.groupChatId as string)
            ? { ...gc, last_message: msg, last_message_at: msg.created_at }
            : gc,
        ),
      };
    }

    case GROUP_CHAT_MESSAGES_NEW: {
      // Polling delivered new messages. Append in order, dedupe by id.
      const incoming = action.messages as ApiGroupChatMessageJSON[];
      const inChat = state.activeGroupChatId === (action.groupChatId as string);
      if (!inChat || incoming.length === 0) {
        return state;
      }
      const existingIds = new Set(state.messages.map((m) => m.id));
      const now = Date.now();
      // Skip already-present ids AND just-deleted ids (a stale overlap poll must
      // not resurrect a locally-deleted message — MED-17 F2).
      const newOnes = incoming.filter(
        (m) =>
          !existingIds.has(m.id) &&
          !isTombstoned(state.deletedMessages, m.id, now),
      );
      if (newOnes.length === 0) return state;
      // Keep messages id-sorted so a recovered lower-id straggler (the overlap
      // cursor) renders in order rather than at the bottom (MED-17 F3, DM parity).
      const merged = [...state.messages, ...newOnes].sort((a, b) =>
        compareId(a.id, b.id),
      );
      const last = merged[merged.length - 1]!;
      return {
        ...state,
        messages: merged,
        list: state.list.map((gc) =>
          gc.id === (action.groupChatId as string)
            ? { ...gc, last_message: last, last_message_at: last.created_at }
            : gc,
        ),
      };
    }

    case GROUP_CHAT_CREATE_SUCCESS: {
      const gc = action.groupChat as ApiGroupChatJSON;
      return {
        ...state,
        list: [gc, ...state.list],
      };
    }

    case GROUP_CHAT_ADD_MEMBERS_SUCCESS: {
      const updated = action.groupChat as ApiGroupChatJSON;
      return {
        ...state,
        list: state.list.map((gc) => (gc.id === updated.id ? updated : gc)),
      };
    }

    case GROUP_CHAT_UPDATE_SUCCESS: {
      // Avatar/name update — replace the row, and RE-SEED read positions:
      // read_states ride along on the serialized chat. Same no-regress rule
      // as GROUP_CHATS_FETCH_SUCCESS.
      const updated = action.groupChat as ApiGroupChatJSON;
      const memberReadPositions = { ...state.memberReadPositions };
      for (const rs of updated.read_states ?? []) {
        const existing = memberReadPositions[updated.id]?.[rs.account_id];
        if (existing !== undefined) {
          const [a, b] = [BigInt(existing), BigInt(rs.last_read_message_id)];
          if (a >= b) continue;
        }
        memberReadPositions[updated.id] = {
          ...memberReadPositions[updated.id],
          [rs.account_id]: rs.last_read_message_id,
        };
      }
      return {
        ...state,
        list: state.list.map((gc) => (gc.id === updated.id ? updated : gc)),
        memberReadPositions,
      };
    }

    case GROUP_CHAT_LEAVE_SUCCESS:
      return {
        ...state,
        list: state.list.filter(
          (gc) => gc.id !== (action.groupChatId as string),
        ),
        activeGroupChatId:
          state.activeGroupChatId === (action.groupChatId as string)
            ? null
            : state.activeGroupChatId,
        messages:
          state.activeGroupChatId === (action.groupChatId as string)
            ? []
            : state.messages,
      };

    case GROUP_CHAT_DELETE_SUCCESS:
      return {
        ...state,
        list: state.list.filter(
          (gc) => gc.id !== (action.groupChatId as string),
        ),
        activeGroupChatId:
          state.activeGroupChatId === (action.groupChatId as string)
            ? null
            : state.activeGroupChatId,
        messages:
          state.activeGroupChatId === (action.groupChatId as string)
            ? []
            : state.messages,
      };

    case GROUP_CHAT_MESSAGE_DELETE_SUCCESS: {
      const now = Date.now();
      return {
        ...state,
        messages: state.messages.filter(
          (m) => m.id !== (action.messageId as string),
        ),
        // Tombstone so a racing in-flight overlap poll cannot re-add it (F2).
        deletedMessages: {
          ...pruneDeletedMessages(state.deletedMessages, now),
          [action.messageId as string]: now,
        },
      };
    }

    case GROUP_CHAT_MESSAGES_CLEAR:
      return {
        ...state,
        activeGroupChatId: null,
        messages: [],
        messagesHasMore: true,
      };

    case GROUP_CHAT_MESSAGE_REACTION_SUCCESS: {
      const updated = action.message as ApiGroupChatMessageJSON;
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === updated.id ? { ...m, reactions: updated.reactions } : m,
        ),
      };
    }

    case GROUP_CHAT_MESSAGE_REACTION_UPDATE: {
      const {
        message_id,
        emoji,
        action: rxAction,
        reactor_account_id,
        currentAccountId,
      } = action.payload as {
        message_id: string;
        emoji: string;
        action: string;
        reactor_account_id: string;
        currentAccountId: string;
      };
      const isMine = reactor_account_id === currentAccountId;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== message_id) return msg;
          const reactions = (msg.reactions ?? []).slice();
          const existing = reactions.find((r) => r.emoji === emoji);
          if (rxAction === 'added') {
            // Idempotent: the originating tab already applied this via
            // REACTION_SUCCESS (me=true) — skip its own echo to avoid
            // double-counting. A SIBLING device of the same account has NOT
            // applied it (me=false/absent), so the self push still lands here.
            // (A blanket `if (isMine) return state` previously dropped self
            // reactions on every device, so they never reached sibling devices.)
            if (existing) {
              if (isMine && existing.me) return msg;
              return {
                ...msg,
                reactions: reactions.map((r) =>
                  r.emoji === emoji
                    ? { ...r, count: r.count + 1, me: r.me || isMine }
                    : r,
                ),
              };
            }
            return {
              ...msg,
              reactions: [...reactions, { emoji, count: 1, me: isMine }],
            };
          } else {
            if (!existing) return msg;
            if (isMine && !existing.me) return msg;
            const newCount = existing.count - 1;
            if (newCount <= 0) {
              return {
                ...msg,
                reactions: reactions.filter((r) => r.emoji !== emoji),
              };
            }
            return {
              ...msg,
              reactions: reactions.map((r) =>
                r.emoji === emoji
                  ? { ...r, count: newCount, me: isMine ? false : r.me }
                  : r,
              ),
            };
          }
        }),
      };
    }

    case GROUP_CHAT_MESSAGE_EDIT_SUCCESS:
    case GROUP_CHAT_MESSAGE_EDITED_REMOTE: {
      const updated = action.message as ApiGroupChatMessageJSON;
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === updated.id ? { ...m, ...updated } : m,
        ),
      };
    }

    case GROUP_CHATS_UNREAD_COUNT_SUCCESS:
      return { ...state, unreadCount: action.count as number };

    case GROUP_CHAT_MEMBERS_ADDED: {
      const p = action.payload as {
        group_chat_id: string;
        added_account_ids: string[];
        added_by_account_id: string;
      };
      const existing = state.memberNotices[p.group_chat_id] ?? [];
      const notice = {
        id: `${p.group_chat_id}:${Date.now()}:${p.added_account_ids.join(',')}`,
        addedByAccountId: p.added_by_account_id,
        addedCount: p.added_account_ids.length,
        at: Date.now(),
      };
      return {
        ...state,
        memberNotices: {
          ...state.memberNotices,
          // Bounded: keep the newest 20 notices per group.
          [p.group_chat_id]: [...existing, notice].slice(-20),
        },
      };
    }

    case GROUP_READ_RECEIPT: {
      const p = action.payload as {
        group_chat_id: string;
        reader_account_id: string;
        last_read_message_id: string;
      };
      return {
        ...state,
        memberReadPositions: {
          ...state.memberReadPositions,
          [p.group_chat_id]: {
            ...(state.memberReadPositions[p.group_chat_id] ?? {}),
            [p.reader_account_id]: p.last_read_message_id,
          },
        },
      };
    }

    case GC_TYPING: {
      const gcId = action.groupChatId as string;
      const acId = action.accountId as string;
      const existing = state.typingAccounts[gcId] ?? [];
      if (existing.includes(acId)) return state;
      return {
        ...state,
        typingAccounts: {
          ...state.typingAccounts,
          [gcId]: [...existing, acId],
        },
      };
    }

    case GC_TYPING_CLEAR: {
      const gcId = action.groupChatId as string;
      const acId = action.accountId as string;
      return {
        ...state,
        typingAccounts: {
          ...state.typingAccounts,
          [gcId]: (state.typingAccounts[gcId] ?? []).filter(
            (id) => id !== acId,
          ),
        },
      };
    }

    default:
      return state;
  }
};
